.volume {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 2px 4px;
  border-radius: var(--radius\/3xl, 360px);
  transition: background-color 0.15s ease;
}

.volume:hover,
.volume[data-slider-open] {
  background-color: rgba(49, 75, 153, 0.5);
}

.volume:hover .btn,
.volume[data-slider-open] .btn {
  background-color: transparent;
}

.btnWrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn:hover + [role="tooltip"],
.btn:focus-visible + [role="tooltip"] {
  opacity: 1;
  pointer-events: auto;
}

.btn {
  composes: controlBtn from '../styles/controls.module.css';
}

/* Icon states */
.iconHigh { display: block; }
.iconLow { display: none; }
.iconMute { display: none; }

.volLow .iconHigh { display: none; }
.volLow .iconLow { display: block; }

.volMute .iconHigh { display: none; }
.volMute .iconMute { display: block; }

/* Slider */
.sliderWrap {
  position: relative;
  width: 126px;
  padding: 0 var(--spacing\/1, 8px);
  display: none;
  align-items: center;
  align-self: stretch;
  cursor: pointer;
}

/* Show slider on desktop hover, self-hover (keeps vertical slider alive when
   the cursor moves from .volume into the absolute-positioned .sliderWrap),
   or touch-open */
.volume:hover .sliderWrap,
.volume[data-slider-open] .sliderWrap,
.sliderWrap:hover {
  display: flex;
}

.slider {
  width: 100%;
  position: relative;
  cursor: pointer;
}

.track {
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: var(--radius\/3xl, 360px);
}

.fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: var(--vol-pct, 0%);
  background: #4F98FF;
  border-radius: var(--radius\/3xl, 360px);
}

.handle {
  position: absolute;
  top: 50%;
  left: var(--vol-pct, 0%);
  width: 16px;
  height: 16px;
  transform: translate(-50%, -50%);
  background: var(--color\/text\/text-primary, #fff);
  border-radius: 50%;
  cursor: pointer;
}

/* Compact width tier (≤ 675px): vertical sound bar */
@container (max-width: 675px) {
  /* Keep the pill in-flow (row); slider floats absolutely above it */
  .volume {
    position: relative;
  }

  /* In vertical mode the ::before pseudo-element renders the full unified pill
     (slider above + button below), so we suppress the button-only horizontal pill */
  .volume:hover,
  .volume[data-slider-open] {
    background-color: transparent;
  }

  /* Pill background spanning from the top of the floating slider down through the button.
     Height = 8px top padding + 126px slider + 8px bottom padding + full volume height.
     Invisible by default; fades in on hover or touch-open. */
  .volume::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: calc(126px + var(--spacing\/1, 8px) * 2 + 100%);
    background-color: rgba(49, 75, 153, 0.5);
    border-radius: var(--radius\/3xl, 360px);
    z-index: -1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  .volume:hover::before,
  .volume[data-slider-open]::before {
    opacity: 1;
  }

  /* Slider floats above the button — does not push sibling controls */
  .sliderWrap {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 126px;
    padding: var(--spacing\/1, 8px) 0;
    flex-direction: column;
    align-items: center;
    /* Sit above the ::after hover-bridge so the slider receives events first */
    z-index: 1;
  }

  /* Transparent hover-bridge: extends .volume's pointer-event area upward to
     cover the floating slider. Without this, cursor entering the pill from
     above only hits ::before (pointer-events:none), never triggering :hover. */
  .volume::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: calc(126px + var(--spacing\/1, 8px) * 2);
  }

  /* Tooltip would overlap the vertical slider above the button — hide it */
  .btnWrapper [role="tooltip"] {
    display: none;
  }

  .slider {
    height: 100%;
    width: auto;
  }

  .track {
    width: 4px;
    height: 100%;
  }

  /* Fills upward: bottom-anchored, height = vol% */
  .fill {
    top: auto;
    bottom: 0;
    left: 0;
    width: 100%;
    height: var(--vol-pct, 0%);
    border-radius: var(--radius\/3xl, 360px);
  }

  /* Handle moves vertically: bottom = vol%, centered horizontally */
  .handle {
    top: auto;
    left: 50%;
    bottom: var(--vol-pct, 0%);
    transform: translate(-50%, 50%);
  }
}

/* Horizontal mode (big tier > 675px container width): slider always visible */
@container (min-width: 676px) {
  .volume {
    padding-right: 16px;
  }

  /* Keep button background neutral inside the pill on hover */
  .volume:hover .btn,
  .volume[data-slider-open] .btn {
    background-color: transparent;
  }

  .sliderWrap {
    display: flex;
  }
}
